import React, { useState, useEffect } from 'react';
import { useAutosave } from '../../../hooks/useAutosave';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Save, Plus, Trash2, Star, CheckCircle, Send, Mail, Clock, X, Copy, FileSignature, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReplacementForm = ({ caseId, replacement, agent, onUpdate }) => {
  const [data, setData] = useState(replacement || {});
  const [saving, setSaving] = useState(false);
  useAutosave(() => save(), data);
  const [generating, setGenerating] = useState(false);
  const [reasonsCatalog, setReasonsCatalog] = useState([]);

  useEffect(() => { setData(replacement || {}); }, [replacement]);

  useEffect(() => {
    fetch(`${API_URL}/api/compliance/reference/replacement-reasons`)
      .then(r => r.ok ? r.json() : [])
      .then(setReasonsCatalog)
      .catch(err => console.error('Failed to fetch replacement reasons:', err));
  }, []);

  const toggleReason = (value) => {
    const current = data.replacement_reasons || [];
    const next = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setData({ ...data, replacement_reasons: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/compliance/cases/${caseId}/replacement?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      onUpdate();
    } finally { setSaving(false); }
  };

  const generateWithAI = async () => {
    if (!data.old_carrier) {
      alert('Please fill in at least the existing carrier and old product type before generating AI text.');
      return;
    }
    // Save current state first so AI sees latest fields
    await fetch(`${API_URL}/api/compliance/cases/${caseId}/replacement?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
    });
    setGenerating(true);
    try {
      const res = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/ai-replacement?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'AI generation failed');
      }
      const { fields } = await res.json();
      const merged = { ...data, ...fields };
      setData(merged);
      // Backend persisted AI fields; re-PUT to ensure full record (including any pre-AI edits) is saved
      await fetch(`${API_URL}/api/compliance/cases/${caseId}/replacement?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(merged)
      });
      onUpdate && onUpdate();
      alert('AI draft generated and saved. Review and edit if needed.');
    } catch (e) {
      alert(`AI generation failed: ${e.message}`);
    } finally { setGenerating(false); }
  };

  const moneyField = (key, label) => (
    <div key={key}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input type="number" value={data[key] || ''} onChange={(e) => setData({ ...data, [key]: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg" data-testid={`rep-${key}`} />
    </div>
  );

  const textField = (key, label, type='text') => (
    <div key={key}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input type={type} value={data[key] || ''} onChange={(e) => setData({ ...data, [key]: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid={`rep-${key}`} />
    </div>
  );

  const textArea = (key, label) => (
    <div key={key}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <textarea rows="2" value={data[key] || ''} onChange={(e) => setData({ ...data, [key]: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid={`rep-${key}`} />
    </div>
  );

  const selectedReasons = data.replacement_reasons || [];

  return (
    <Card data-testid="replacement-card">
      <CardHeader className="flex flex-row items-center justify-end flex-wrap gap-2">
        <div className="flex gap-2">
          <Button onClick={generateWithAI} disabled={generating} variant="outline" className="border-[#D4AF37] text-[#003D5C]" data-testid="ai-replacement-btn">
            <Sparkles className="h-4 w-4 mr-2" />{generating ? 'Generating...' : 'Generate with AI'}
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#0077B6]" data-testid="save-replacement-btn">
            <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <h4 className="font-semibold pb-2 border-b mb-3">Existing Policy Being Replaced</h4>
          <div className="grid md:grid-cols-2 gap-3">
            {textField('old_carrier', 'Old Carrier *')}
            {textField('old_policy_number', 'Old Policy Number')}
            {textField('old_product_type', 'Old Product Type *')}
            {textField('old_issue_date', 'Old Issue Date', 'date')}
            {moneyField('old_face_amount', 'Old Face Amount')}
            {moneyField('old_premium', 'Old Premium')}
            {moneyField('old_cash_value', 'Old Cash Value')}
            {moneyField('old_surrender_charges', 'Surrender Charges')}
            {textField('old_riders', 'Old Riders')}
            {textField('old_guarantees', 'Old Guarantees')}
            {textField('old_conversion_privileges', 'Conversion Privileges')}
          </div>
        </div>

        <div>
          <h4 className="font-semibold pb-2 border-b mb-3">
            Reasons for Replacement
            <span className="ml-2 text-xs font-normal text-gray-500">
              ({selectedReasons.length} selected — pick all that apply)
            </span>
          </h4>
          <div className="grid md:grid-cols-2 gap-2" data-testid="replacement-reasons-grid">
            {reasonsCatalog.map(r => (
              <label key={r.value} className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition ${
                selectedReasons.includes(r.value)
                  ? 'bg-blue-50 border-[#0077B6]'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}>
                <input
                  type="checkbox"
                  checked={selectedReasons.includes(r.value)}
                  onChange={() => toggleReason(r.value)}
                  className="mt-1"
                  data-testid={`rep-reason-${r.value}`}
                />
                <span className="text-sm">{r.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-semibold pb-2 border-b mb-3">Disclosures & Notes</h4>
          {textArea('tax_consequences', 'Tax Consequences of Replacement')}
          {textArea('consequences_of_cancellation', 'Consequences of Cancellation')}
          {textArea('comparative_analysis', 'Written Comparative Analysis')}
          {textArea('replacement_rationale', 'Additional Rationale Notes (optional, supplements selected reasons above)')}
        </div>

        <div className="flex flex-col gap-2 pt-3 border-t">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.lird_completed || false} onChange={(e) => setData({ ...data, lird_completed: e.target.checked })} data-testid="rep-lird-completed" />
            <span className="text-sm">LIRD (Life Insurance Replacement Declaration) Completed</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.comparative_completed || false} onChange={(e) => setData({ ...data, comparative_completed: e.target.checked })} data-testid="rep-comparative-completed" />
            <span className="text-sm">Comparative Analysis Completed</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.client_warning_acknowledged || false} onChange={(e) => setData({ ...data, client_warning_acknowledged: e.target.checked })} data-testid="rep-client-warning" />
            <span className="text-sm">Client Replacement Warning Acknowledged</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.supervisor_approved || false} onChange={(e) => setData({ ...data, supervisor_approved: e.target.checked })} data-testid="rep-supervisor-approved" />
            <span className="text-sm">Supervisor Approved Replacement</span>
          </label>
        </div>
      </CardContent>
    </Card>
  );
};

// =============================================================
// AML / KYC Form
// =============================================================
